import { MiButton } from "@miplatform/components";
import { TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { mdiChevronDown, mdiChevronUp } from "@mdi/js";
import { Box, Divider, Typography, useTheme } from "@mui/material";

/**
 * A header component for navigation groups in the sidebar that displays the group name
 * and optional collapse/expand functionality.
 *
 * @param {Object} props - The component props
 * @param {string} props.groupName - The name of the navigation group to display
 * @param {boolean} props.isMainNavCollapsed - Whether the main navigation is collapsed
 * @param {boolean} [props.isGroupCollapsed=false] - Whether this specific group is collapsed
 * @param {Function|null} [props.toggleCollapse=null] - Callback function to toggle group collapse state
 * @returns {JSX.Element} The rendered side navigation group header component
 */
export function SideNavGroupHeader({
  groupName,
  isMainNavCollapsed,
  isGroupCollapsed = false,
  toggleCollapse = null,
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "center",
        height: 36,
        padding: !isMainNavCollapsed
          ? theme.spacing(3, 1, 2, 2)
          : theme.spacing(3, 2, 2, 2),
      }}
    >
      {!isMainNavCollapsed ? (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            cursor: toggleCollapse ? "pointer" : "default",
            userSelect: "none",
          }}
          onClick={toggleCollapse}
        >
          <Typography
            variant={TYPOGRAPHY_VARIANTS.BD1}
            fontWeight={theme.fontWeight.medium}
            color={theme.palette.grey.darkAccent}
            textTransform="uppercase"
          >
            {groupName}
          </Typography>
          {toggleCollapse && (
            <MiButton
              data-testid="collapse-and-expand-icon"
              startIcon={{
                path: isGroupCollapsed ? mdiChevronUp : mdiChevronDown,
                iconColor: theme.palette.grey.medium,
                iconSize: 0.8,
              }}
              sx={{
                padding: 0,
              }}
              disableRipple
            />
          )}
        </Box>
      ) : (
        <Divider />
      )}
    </Box>
  );
}
